/*
Vehicle Audit Hook
Custom hook for fetching and managing vehicle audit data
Author: System
Date: 2025-01-27
*/

import { useState, useCallback } from 'react'

import { customFetch } from '@/utils/customFetch'

interface AuditLogEntry {
  _id: string
  entityType: 'vehicle'
  entityId: string
  action: 'create' | 'update' | 'delete' | 'archive' | 'restore' | 'publish' | 'unpublish'
  changes: any[]
  previousData?: any
  newData?: any
  userId: string
  userEmail: string
  organizationId: string
  timestamp: string
  changesSummary: string[]
  metadata?: {
    route?: string
    userAgent?: string
    ip?: string
    reason?: string
    platform?: 'yapo' | 'chileautos' | 'meli'
  }
}

interface AuditSummary {
  totalEntries: number
  summary: Array<{
    _id: {
      action: string
      date: string
    }
    count: number
    users: string[]
  }>
  activeUsers: Array<{
    _id: string
    totalChanges: number
    actions: string[]
    lastActivity: string
  }>
  mostChangedVehicles: Array<{
    vehicleId: string
    totalChanges: number
    actions: string[]
    lastChange: string
    users: string[]
    plate: string
    brand: string
    model: string
    year: number
  }>
}

interface AuditSearchParams {
  action?: string
  userEmail?: string
  startDate?: string
  endDate?: string
  vehicleId?: string
  limit?: number
  skip?: number
}

interface AuditSearchResult {
  results: (AuditLogEntry & {
    vehicle?: {
      plate: string
      brand: string
      model: string
      year: number
    }
  })[]
  pagination: {
    total: number
    limit: number
    skip: number
    hasMore: boolean
  }
}

export const useVehicleAudit = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Fetch audit history for a specific vehicle
   */
  const fetchVehicleHistory = useCallback(async (vehicleId: string, limit: number = 100): Promise<AuditLogEntry[]> => {
    setLoading(true)
    setError(null)

    try {
      const response = await customFetch<{
        success: boolean
        history: AuditLogEntry[]
        count: number
      }>(`${import.meta.env.VITE_API_URL}vehicles/${vehicleId}/audit-history?limit=${limit}`)

      if (response.success) {
        return response.history
      } else {
        throw new Error('Failed to fetch audit history')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Fetch audit summary for the organization
   */
  const fetchAuditSummary = useCallback(async (startDate?: string, endDate?: string): Promise<AuditSummary> => {
    setLoading(true)
    setError(null)

    try {
      const params = new URLSearchParams()

      if (startDate) params.append('startDate', startDate)

      if (endDate) params.append('endDate', endDate)

      const response = await customFetch<
        {
          success: boolean
        } & AuditSummary
      >(`${import.meta.env.VITE_API_URL}audit/summary?${params.toString()}`)

      if (response.success) {
        return {
          totalEntries: response.totalEntries,
          summary: response.summary,
          activeUsers: response.activeUsers,
          mostChangedVehicles: response.mostChangedVehicles,
        }
      } else {
        throw new Error('Failed to fetch audit summary')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Search audit logs with filters
   */
  const searchAuditLogs = useCallback(async (params: AuditSearchParams): Promise<AuditSearchResult> => {
    setLoading(true)
    setError(null)

    try {
      const searchParams = new URLSearchParams()

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString())
        }
      })

      const response = await customFetch<
        {
          success: boolean
        } & AuditSearchResult
      >(`${import.meta.env.VITE_API_URL}audit/search?${searchParams.toString()}`)

      if (response.success) {
        return {
          results: response.results,
          pagination: response.pagination,
        }
      } else {
        throw new Error('Failed to search audit logs')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Fetch audit statistics
   */
  const fetchAuditStats = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await customFetch<{
        success: boolean
        stats: {
          totalAudits: number
          auditsLast30Days: number
          actionBreakdown: Array<{
            _id: string
            count: number
          }>
          dailyActivity: Array<{
            date: string
            count: number
            uniqueUsers: number
          }>
        }
      }>(`${import.meta.env.VITE_API_URL}audit/stats`)

      if (response.success) {
        return response.stats
      } else {
        throw new Error('Failed to fetch audit stats')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loading,
    error,
    fetchVehicleHistory,
    fetchAuditSummary,
    searchAuditLogs,
    fetchAuditStats,
  }
}

export default useVehicleAudit
